import {
  ITEM_TYPES,
  bestTeam,
  chapterIndex,
  currentEnergy,
  claimableTiers,
  rollsLeft,
  betsLeft,
  leagues,
  dailyStatus,
  difficulties,
  difficultyKey,
  hasOpenTeamSlot,
  missionStatus,
  teamHp,
  teamPower,
  upgradableCount,
  type ItemType,
  type BotUser,
  type Session,
} from '@fakerpg/core';
import { useNow, useSave } from '../game/context.js';
import type { MessageKey } from '../i18n/index.js';
import type { RouteName } from '../router.js';

/** Energy including regeneration not yet applied by a `tick`. */
export function useLiveEnergy(): number {
  const save = useSave();
  const now = useNow(5000);
  return currentEnergy(save, now);
}

export const TYPE_KEYS: Record<ItemType, MessageKey> = {
  characters: 'types.characters',
  weapons: 'types.weapons',
  shields: 'types.shields',
  pets: 'types.pets',
};

export const SINGULAR_KEYS: Record<ItemType, MessageKey> = {
  characters: 'types.character',
  weapons: 'types.weapon',
  shields: 'types.shield',
  pets: 'types.pet',
};

export function stage(chapter: number, step: number): string {
  return `${chapter + 1}-${step + 1}`;
}

/**
 * `bestTeam` ranks every owned item: too costly for the top bar (1 s timer) and
 * the nav badges (5 s). A save object never changes once made (each dispatch
 * returns a new one), so results are cached per save.
 */
function perSave<T>(compute: (save: BotUser) => T): (save: BotUser) => T {
  const cache = new WeakMap<BotUser, T>();
  return (save) => {
    if (cache.has(save)) return cache.get(save) as T;
    const value = compute(save);
    cache.set(save, value);
    return value;
  };
}

/** Character shown as the player's avatar: team slot 1, else the best owned one. */
export const leadCharacter = perSave((save: BotUser): number | null => {
  const slot = save.summoned.team.equiped[0]?.characters;
  if (slot) return slot;
  const best = bestTeam(save)[0]?.characters;
  return best ? best : null;
});

/** Next playable stage on a difficulty, or null when everything is cleared. */
export function frontier(save: BotUser, difficulty: number): { chapter: number; step: number } | null {
  const p = save.adventures[difficultyKey(difficulty)];
  if (p.chapter >= chapterIndex.length) return null;
  return { chapter: p.chapter, step: p.step };
}

export function greetingKey(hour: number): MessageKey {
  if (hour >= 5 && hour < 12) return 'greeting.morning';
  if (hour >= 12 && hour < 18) return 'greeting.afternoon';
  if (hour >= 18 && hour < 23) return 'greeting.evening';
  return 'greeting.night';
}

/** Power a re-optimized team would have minus the current one (≥ 0). */
export const optimizeGain = perSave((save: BotUser): number => {
  const optimized = { ...save, summoned: { ...save.summoned, team: { equiped: bestTeam(save) } } };
  return Math.max(0, teamPower(optimized) + Math.floor(teamHp(optimized) / 100) - (teamPower(save) + Math.floor(teamHp(save) / 100)));
});

export interface NavBadge {
  count?: number;
  dot?: boolean;
}

/** Live "something to do here" markers for the navigation. */
export function navBadges(save: BotUser, session: Session, now: number): Partial<Record<RouteName, NavBadge>> {
  const energy = currentEnergy(save, now);
  const cheapest = Math.min(...difficulties.map((d) => d.cost));
  const upgrades = ITEM_TYPES.reduce((a, t) => a + upgradableCount(save, t), 0);
  const mission = missionStatus(save, now);
  const daily = dailyStatus(save, now).state === 'available';
  return {
    home: { dot: daily || mission === 'done' || claimableTiers(save) > 0 },
    adventures: { dot: !!session.run || energy >= cheapest },
    summon: { count: save.lootbox },
    team: { dot: hasOpenTeamSlot(save) || optimizeGain(save) > 0 },
    collection: { count: upgrades },
    missions: { dot: mission !== 'running' },
    daily: { dot: daily },
    quests: claimableTiers(save) > 0 ? { count: claimableTiers(save) } : {},
    attributes: { dot: rollsLeft(save, now) > 0 },
    games: { dot: betsLeft(save, now) > 0 || !!session.spot },
  };
}

/** i18n key of an arena league name. */
export function leagueKey(rank: number): MessageKey {
  return `arena.leagues.${leagues[Math.min(rank, leagues.length - 1)]?.name ?? 'bronze'}` as MessageKey;
}
